import React, { useState, useCallback } from 'react';
import { ChevronDown, ChevronRight, Folder, FolderOpen, X } from 'lucide-react';
import styles from './CategoryFilter.module.css';

const CategoryFilter = ({ 
  categories = [], 
  selectedCategories = [], 
  onCategoryChange,
  className = '',
  showResourceCount = true,
  collapsible = true,
  showCloseButton = false,
  onClose
}) => {
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Toggle category expansion
  const toggleCategoryExpansion = useCallback((categoryId) => {
    if (!collapsible) return;
    
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, [collapsible]);

  // Handle category selection
  const handleCategorySelect = useCallback((categorySlug, isSelected) => {
    if (!onCategoryChange) return;

    let newSelectedCategories;
    if (isSelected) {
      // Remove category from selection
      newSelectedCategories = selectedCategories.filter(slug => slug !== categorySlug);
    } else {
      // Add category to selection
      newSelectedCategories = [...selectedCategories, categorySlug];
    }
    
    onCategoryChange(newSelectedCategories);
  }, [selectedCategories, onCategoryChange]);

  // Clear all selections
  const handleClearAll = useCallback(() => {
    if (onCategoryChange) {
      onCategoryChange([]);
    }
  }, [onCategoryChange]);

  // Expand all categories
  const handleExpandAll = useCallback(() => {
    if (!collapsible) return;
    
    const allCategoryIds = new Set();
    const collectIds = (cats) => {
      cats.forEach(cat => {
        if (cat.hasChildren()) {
          allCategoryIds.add(cat.id);
          collectIds(cat.children);
        }
      });
    };
    collectIds(categories);
    setExpandedCategories(allCategoryIds);
  }, [categories, collapsible]);

  // Collapse all categories
  const handleCollapseAll = useCallback(() => {
    if (!collapsible) return;
    setExpandedCategories(new Set());
  }, [collapsible]);

  // Flatten categories for grid layout
  const flattenCategories = (categories, level = 0, parentExpanded = true) => {
    return categories.reduce((acc, category) => {
      const isExpanded = expandedCategories.has(category.id);
      const hasChildren = category.hasChildren();
      
      // Only include if parent is expanded or it's a top-level category
      if (level === 0 || parentExpanded) {
        acc.push({ ...category, level, isExpanded, hasChildren });
        }
      
      // Add children if expanded
      if (hasChildren && (isExpanded || !collapsible)) {
        acc.push(...flattenCategories(category.children, level + 1, isExpanded));
      }
      
      return acc;
    }, []);
  };

  // Render individual category item
  const renderCategoryItem = (category) => {
    const isSelected = selectedCategories.includes(category.slug);
    const indentLevel = category.level * 20;

    return (
      <div 
        key={category.id} 
        className={styles.categoryItem}
        style={{ marginLeft: `${indentLevel}px` }}
      >
        {/* Category pill button */}
        <button
          type="button"
          onClick={() => handleCategorySelect(category.slug, isSelected)}
          className={`${styles.categoryPill} ${isSelected ? styles.selected : ''}`}
          aria-pressed={isSelected}
        >
          {category.hasChildren && (
            <span 
              className={styles.categoryIcon}
              onClick={(e) => {
                e.stopPropagation();
                toggleCategoryExpansion(category.id);
              }}
              aria-label={`${category.isExpanded ? 'Collapse' : 'Expand'} ${category.name} category`}
            >
              {category.isExpanded ? (
                <FolderOpen size={14} aria-hidden="true" />
              ) : (
                <Folder size={14} aria-hidden="true" />
              )}
            </span>
          )}
          <span className={styles.categoryName}>{category.name}</span>
        </button>
      </div>
    );
  };

  // Don't render if no categories
  if (!categories || categories.length === 0) {
    return (
      <div className={`${styles.categoryFilter} ${className}`}>
        <div className={styles.header}>
          <h3>Tags</h3>
        </div>
        <div className={styles.emptyState}>
          <p>No tags available</p>
        </div>
      </div>
    );
  }

  const hasSelections = selectedCategories.length > 0;
  const hasExpandableCategories = categories.some(cat => cat.hasChildren());

  return (
    <div className={`${styles.categoryFilter} ${className}`}>
      {/* Header with controls */}
      <div className={styles.header}>
        <h3>Tags</h3>
        <div className={styles.headerControls}>
          {hasSelections && (
            <button
              type="button"
              onClick={handleClearAll}
              className={styles.clearButton}
              aria-label="Clear all tag selections"
            >
              Clear All
            </button>
          )}
          {showCloseButton && onClose && (
            <button
              type="button"
              onClick={onClose}
              className={styles.closeButton}
              aria-label="Close tag filters"
            >
              <X size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Expansion controls */}
      {collapsible && hasExpandableCategories && (
        <div className={styles.expansionControls}>
          <button
            type="button"
            onClick={handleExpandAll}
            className={styles.expansionButton}
            aria-label="Expand all categories"
          >
            Expand All
          </button>
          <button
            type="button"
            onClick={handleCollapseAll}
            className={styles.expansionButton}
            aria-label="Collapse all categories"
          >
            Collapse All
          </button>
        </div>
      )}

      {/* Selected tags summary */}
      {hasSelections && (
        <div className={styles.selectionSummary} aria-live="polite">
          <span className={styles.selectionCount}>
            {selectedCategories.length} tag{selectedCategories.length === 1 ? '' : 's'} selected
          </span>
        </div>
      )}

      {/* Tags list */}
      <div className={styles.categoriesList} role="group" aria-label="Tag filters">
        {flattenCategories(categories).map(category => renderCategoryItem(category))}
      </div>

      {/* Screen reader instructions */}
      <div className={styles.srOnly}>
        Use tag buttons to filter resources. Press buttons to toggle tag selection.
        {collapsible && 'Use expand/collapse buttons to show or hide subcategories.'}
        Selected tags will be applied to the resource list.
      </div>
    </div>
  );
};

export default CategoryFilter;